import { useState, useMemo } from 'react';
import { 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Building2, 
  Filter, 
  Loader2, 
  Calendar, 
  User, 
  Layers, 
  Briefcase,
  ChevronDown,
  Layout
} from 'lucide-react';
import { cn } from './ui/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { 
  DropdownMenu,
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

import { ConsultantReviewPanel } from './ConsultantReviewPanel';
import { SpacePlanningReviewsList } from './SpacePlanningReviewsList';
import { DashboardActionItems } from './DashboardActionItems';
import { DecisionApprovalsPage } from './DecisionApprovalsPage';
import { usePendingDecisions } from '../hooks/usePendingDecisions';
import { useReviewQueueUnified, ReviewQueueItem } from '../hooks/useReviewQueueUnified';
import { projectId } from '../utils/supabase/info';
import { reviewItemNeedsAttention } from '../utils/queueCounts';
import type { ActionRow } from '../utils/consultantActionItems';

interface ReviewQueuePanelProps {
  accessToken: string;
  currentUser: any;
  actionItemsData?: any; // Optional preloaded data
}

type GroupBy = 'client' | 'engagement' | 'type';
type QuickFilter = 'needs-review' | 'blocked' | 'recent' | 'assigned';

export function ReviewQueuePanel({ accessToken, currentUser, actionItemsData }: ReviewQueuePanelProps) {
  const { pendingCount: decisionPendingCount } = usePendingDecisions(
    currentUser.id,
    accessToken,
    true
  );

  const { 
    items, 
    loading, 
    refresh, 
    totalCount 
  } = useReviewQueueUnified({ 
    accessToken, 
    currentUser 
  });

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>('client');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('needs-review');
  
  const [selectedItem, setSelectedItem] = useState<ReviewQueueItem | null>(null);
  const [reviewPanelOpen, setReviewPanelOpen] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Filter Items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Default to showing everything if filter logic is complex, but implementing basics:
      if (quickFilter === 'needs-review') {
        // The same rule the Queue's count uses (utils/queueCounts.ts).
        return reviewItemNeedsAttention(item);
      }
      if (quickFilter === 'blocked') return item.status === 'blocked';
      if (quickFilter === 'assigned') return item.assignedToName === currentUser.name;
      
      if (quickFilter === 'recent') {
        if (!item.lastUpdated) return false;
        const date = new Date(item.lastUpdated);
        const now = new Date();
        const diffDays = (now.getTime() - date.getTime()) / (1000 * 3600 * 24);
        return diffDays <= 3;
      }
      
      return true;
    });
  }, [items, quickFilter, currentUser.name]);

  // Group Items
  const groupedItems = useMemo(() => {
    const groups: Record<string, ReviewQueueItem[]> = {};
    
    filteredItems.forEach(item => {
      let key = '';
      if (groupBy === 'client') key = item.companyName;
      else if (groupBy === 'engagement') key = item.engagementName || item.companyName; // Fallback
      else if (groupBy === 'type') key = item.category || 'Other';
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    
    return groups;
  }, [filteredItems, groupBy]);

  const handleToggleItem = (itemKey: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemKey)) {
      newSelected.delete(itemKey);
    } else {
      newSelected.add(itemKey);
    }
    setSelectedItems(newSelected);
  };

  const handleBulkApprove = async () => {
    if (selectedItems.size === 0) return;

    try {
      setBulkApproving(true);
      
      // Convert selected item keys to { companyId, itemId } format
      // Item key format expected: `${companyId}:${id}` from rendering loop
      const itemsToApprove = Array.from(selectedItems).map(key => {
        const [companyId, itemId] = key.split(':');
        return { companyId, itemId };
      });

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/bulk-approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: itemsToApprove }),
        }
      );

      if (response.ok) {
        // Clear selection and reload
        setSelectedItems(new Set());
        refresh();
      }
    } catch (error) {
      console.error('Error bulk approving items:', error);
    } finally {
      setBulkApproving(false);
    }
  };

  // Review from an action-item row: the review queue already holds the full item; otherwise load it.
  const reviewActionRow = async (row: ActionRow) => {
    let item: any = items.find((i) => i.companyId === row.companyId && i.id === row.itemId);
    if (!item) {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${encodeURIComponent(row.companyId)}/${encodeURIComponent(row.itemId)}`,
          { headers: { 'Authorization': `Bearer ${accessToken}` } }
        );
        if (response.ok) item = (await response.json())?.item;
      } catch (error) {
        console.error('Error loading the item to review:', error);
      }
    }
    setSelectedItem({ ...(item ?? { id: row.itemId, name: row.title, type: row.configItemType ?? '' }), companyId: row.companyId, companyName: row.companyName });
    setReviewPanelOpen(true);
  };

  const formatDueDate = (dateStr?: string) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const renderCard = (item: ReviewQueueItem) => {
    const itemKey = `${item.companyId}:${item.id}`;
    const isSelected = selectedItems.has(itemKey);
    
    return (
      <div
        key={itemKey}
        className={`border rounded-lg p-3 hover:bg-gray-50 transition-colors ${
          isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white'
        }`}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => handleToggleItem(itemKey)}
            className="mt-1"
          />

          <div className="flex-1 min-w-0">
            {/* Top Row: Name and Status */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="font-medium text-sm truncate pr-2">{item.name}</h4>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="shrink-0 text-[10px] bg-orange-50 text-orange-700 border-orange-200 uppercase tracking-wide">
                      NEEDS REVIEW
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This item is awaiting consultant validation before implementation.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Secondary Line: Client & Type */}
            <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Building2 className="size-3" />
                <span className="font-medium text-gray-700">{item.companyName}</span>
              </div>
              <span className="text-gray-300">|</span>
              <Badge variant="secondary" className="text-[10px] h-5 uppercase">
                {item.type || 'SOW DELIVERABLE'}
              </Badge>
            </div>

            {/* Description */}
            {item.description && (
              <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                {item.description}
              </p>
            )}

            {/* Meta Row */}
            <div className="flex items-center gap-4 text-[10px] text-muted-foreground mt-2 border-t pt-2 border-dashed">
              {item.engagementName && (
                <div className="flex items-center gap-1">
                  <Briefcase className="size-3" />
                  <span className="truncate max-w-[100px]">{item.engagementName}</span>
                </div>
              )}
              {item.dueDate && (
                <div className={cn(
                  "flex items-center gap-1 font-medium",
                  new Date(item.dueDate) < new Date() ? "text-red-600" : 
                  (new Date(item.dueDate).getTime() - new Date().getTime()) / (1000 * 3600 * 24) <= 3 ? "text-amber-600" : "text-muted-foreground"
                )}>
                  <Calendar className="size-3" />
                  <span>
                    {new Date(item.dueDate) < new Date() ? "Past Due" : "Due"} {formatDueDate(item.dueDate)}
                  </span>
                </div>
              )}
              {item.assignedToName && (
                <div className="flex items-center gap-1">
                  <User className="size-3" />
                  <span>{item.assignedToName}</span>
                </div>
              )}
              {item.lastUpdated && (
                 <div className="ml-auto">
                    Updated {formatDueDate(item.lastUpdated)}
                 </div>
              )}
            </div>
            
            <div className="mt-3 flex justify-end">
               <Button
                  variant="default"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setSelectedItem(item);
                    setReviewPanelOpen(true);
                  }}
                >
                  Review
                </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 1. Action Items Section */}
      <section>
        <DashboardActionItems 
          accessToken={accessToken} 
          currentUser={currentUser} 
          preloadedData={actionItemsData}
          onReview={reviewActionRow}
        />
      </section>

      {/* 2. Client Submissions Section */}
      <section>
         <SpacePlanningReviewsList 
            accessToken={accessToken}
            consultantId={currentUser.id}
         />
      </section>
      
      {/* 3. Review Queue Section */}
      <section>
        <Card>
          <CardHeader className="pb-3">
             <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Clock className="size-5 text-orange-600" />
                    Review Queue
                    {filteredItems.length > 0 && (
                      <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200">
                        {filteredItems.length}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Deliverables awaiting your approval
                  </CardDescription>
                </div>

                <div className="flex items-center gap-2">
                   {/* Group By Toggle */}
                   <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                         <Button variant="outline" size="sm" className="h-8">
                            <Layers className="size-3.5 mr-2" />
                            Group: <span className="font-medium ml-1 capitalize">{groupBy}</span>
                            <ChevronDown className="size-3 ml-1 opacity-50" />
                         </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                         <DropdownMenuItem onClick={() => setGroupBy('client')}>Client</DropdownMenuItem>
                         <DropdownMenuItem onClick={() => setGroupBy('engagement')}>Engagement</DropdownMenuItem>
                         <DropdownMenuItem onClick={() => setGroupBy('type')}>Deliverable Type</DropdownMenuItem>
                      </DropdownMenuContent>
                   </DropdownMenu>

                   {/* Bulk Actions */}
                   {selectedItems.size > 0 && (
                    <Button
                      onClick={handleBulkApprove}
                      disabled={bulkApproving}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 h-8"
                    >
                      {bulkApproving ? (
                        <>
                          <Loader2 className="size-3.5 mr-2 animate-spin" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="size-3.5 mr-2" />
                          Approve ({selectedItems.size})
                        </>
                      )}
                    </Button>
                   )}
                </div>
             </div>

             {/* Quick Filters */}
             <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-1">
                <Button 
                   variant={quickFilter === 'needs-review' ? 'default' : 'ghost'} 
                   size="sm" 
                   className="h-7 text-xs rounded-full"
                   onClick={() => setQuickFilter('needs-review')}
                >
                   Needs Review
                </Button>
                <Button 
                   variant={quickFilter === 'blocked' ? 'default' : 'ghost'} 
                   size="sm" 
                   className="h-7 text-xs rounded-full"
                   onClick={() => setQuickFilter('blocked')}
                >
                   Blocked
                </Button>
                <Button 
                   variant={quickFilter === 'recent' ? 'default' : 'ghost'} 
                   size="sm" 
                   className="h-7 text-xs rounded-full"
                   onClick={() => setQuickFilter('recent')}
                >
                   Recently Updated
                </Button>
                <Button 
                   variant={quickFilter === 'assigned' ? 'default' : 'ghost'} 
                   size="sm" 
                   className="h-7 text-xs rounded-full"
                   onClick={() => setQuickFilter('assigned')}
                >
                   Assigned To Me
                </Button>
             </div>
          </CardHeader>
          
          <CardContent>
            {loading ? (
               <div className="flex items-center justify-center py-12">
                 <Loader2 className="size-8 animate-spin text-gray-300" />
               </div>
            ) : filteredItems.length === 0 ? (
               <div className="flex items-center justify-center gap-2 py-4 text-green-700 bg-green-50 rounded-lg border border-green-100">
                  <CheckCircle2 className="size-5" />
                  <p className="font-medium text-sm">Review queue clear.</p>
               </div>
            ) : (
               <Accordion type="multiple" defaultValue={Object.keys(groupedItems)} className="space-y-4">
                  {Object.entries(groupedItems).map(([group, groupItems]) => (
                     <AccordionItem value={group} key={group} className="border-none">
                        <AccordionTrigger className="hover:no-underline py-2 bg-gray-50/50 px-3 rounded-md mb-2">
                           <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{group}</span>
                              <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[20px] justify-center">{groupItems.length}</Badge>
                           </div>
                        </AccordionTrigger>
                        <AccordionContent>
                           <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 pt-1">
                              {groupItems.map(item => renderCard(item))}
                           </div>
                        </AccordionContent>
                     </AccordionItem>
                  ))}
               </Accordion>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Review Panel Dialog */}
      {selectedItem && (
        <ConsultantReviewPanel
          item={selectedItem}
          companyId={selectedItem.companyId}
          companyName={selectedItem.companyName}
          accessToken={accessToken}
          currentUser={currentUser}
          open={reviewPanelOpen}
          onClose={() => {
            setReviewPanelOpen(false);
            setSelectedItem(null);
          }}
          onUpdate={() => {
            refresh();
            actionItemsData?.refresh?.();
          }}
        />
      )}
    </div>
  );
}
